import { Box, Button, FormControlLabel, Grid2, Paper, Switch, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import StarterKit from "@tiptap/starter-kit";
import { Formik } from 'formik';
import {
  MenuButtonBold,
  MenuButtonItalic,
  MenuControlsContainer,
  MenuDivider,
  MenuSelectHeading,
  RichTextEditor,
  type RichTextEditorRef,
} from "mui-tiptap";
import React from 'react';
import PhoneInput from 'react-phone-input-2';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { updateAccount, updateAccountManagerSettings } from '../../api/account';
import { useTranslationHook } from '../../hooks';
import { useFormStyles } from '../../hooks/useFormStyles';
import { setLoading } from '../../store/UI';
import { Account } from '../../types/model/account';
import { StoreInterfaces } from '../../types/store/StoreInterfaces';

interface AccountInformationProps {
  accountData: Account;
  onRefresh: () => void;
  readOnly: boolean;
};

const AccountInformation = ({
  accountData, 
  onRefresh,
  readOnly
}: AccountInformationProps) => {
  const { t } = useTranslationHook();
  const dispatch = useDispatch();
  const theme = useTheme();
  const formStyles = useFormStyles();
  const session = useSelector((state: StoreInterfaces) => state.session);
  const rteRef = React.useRef<RichTextEditorRef>(null);
  const isAdminUser = session.isAdministrator;

  const [editMode, setEditMode] = React.useState(false);

  const handleSaveAction = (values: {
    accountName: string;
    accountEmail: string;
    accountFiscal: string;
    accountAddress: string;
    accountZipCode: string;
    accountCity: string;
    accountPhoneNumber: string;
    accountDescription: string;
    allowExternalManagers: boolean;
    maxInternalManagers: number | null;
    limitInternalManagers: boolean;
  }) => {
    if (!accountData) return;
    dispatch(setLoading(true));

    const updatedAccount: Account = {
      ...accountData,
      name: values.accountName,
      email: values.accountEmail,
      fiscalNumber: values.accountFiscal,
      address: values.accountAddress,
      zipCode: values.accountZipCode,
      city: values.accountCity,
      phoneNumber: values.accountPhoneNumber,
      description: values.accountDescription,
    };

    const promises = [updateAccount(accountData.id, updatedAccount)];
    if (isAdminUser) {
      promises.push(updateAccountManagerSettings(accountData.id, {
        allowExternalManagers: values.allowExternalManagers,
        maxInternalManagers: values.limitInternalManagers ? values.maxInternalManagers : null,
      }));
    }

    Promise.all(promises)
      .then(() => {
        setEditMode(false);
        onRefresh();
      })
      .catch(console.error)
      .finally(() => dispatch(setLoading(false)));
  };

  const showEditButton = !editMode && !readOnly;
  const showSaveActions = editMode && !readOnly;

  return (
    <Box sx={{ pt: 2 }}>
      {accountData && (
        <Formik
          initialValues={{
            accountName: accountData.name,
            accountEmail: accountData.email,
            accountFiscal: accountData.fiscalNumber,
            accountAddress: accountData.address,
            accountZipCode: accountData.zipCode,
            accountCity: accountData.city,
            accountPhoneNumber: accountData.phoneNumber,
            accountDescription: accountData.description || '',
            allowExternalManagers: accountData.allowExternalManagers,
            maxInternalManagers: accountData.maxInternalManagers ?? null,
            limitInternalManagers: accountData.maxInternalManagers !== null,
          }}
          onSubmit={handleSaveAction}
          validationSchema={Yup.object().shape({
            accountName: Yup.string().required(t('admin.accounts.account-name-required')),
            accountEmail: Yup.string()
              .email(t('admin.accounts.account-email-valid'))
              .required(t('admin.accounts.account-email-required')),
            accountFiscal: Yup.string().required(t('admin.accounts.account-fiscal-number-required')),
            accountAddress: Yup.string(),
            accountZipCode: Yup.string(),
            accountCity: Yup.string(),
            accountDescription: Yup.string(),
            accountPhoneNumber: Yup.string().required(t('admin.accounts.account-phone-number-required')),
            allowExternalManagers: Yup.boolean(),
            limitInternalManagers: Yup.boolean(),
            maxInternalManagers: Yup.number()
              .nullable()
              .transform((value, originalValue) => originalValue === '' ? null : value)
              .when('limitInternalManagers', {
                is: true,
                then: (schema) => schema
                  .required(t('admin.accounts.max-internal-managers-required'))
                  .min(0, t('admin.accounts.max-internal-managers-min')),
                otherwise: (schema) => schema.nullable(),
              }),
          })}
        >
          {({ values, touched, errors, handleChange, handleBlur, handleSubmit, setFieldValue, resetForm }) => (
            <Box component="form" onSubmit={handleSubmit} sx={{ p: 1 }}>
              <Grid2 container spacing={2}>

                {/* Name */}
                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    id="accountName"
                    label={t('admin.accounts.name')}
                    variant={editMode ? 'outlined' : 'standard'}
                    disabled={!editMode}
                    value={values.accountName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!(errors.accountName && touched.accountName)}
                    helperText={errors.accountName || ''}
                    sx={{ ...formStyles }}
                  />
                </Grid2>

                {/* Fiscal */}
                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    id="accountFiscal"
                    label={t('admin.accounts.fiscal-number')}
                    variant={editMode ? 'outlined' : 'standard'}
                    disabled={!editMode}
                    value={values.accountFiscal}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!(errors.accountFiscal && touched.accountFiscal)}
                    helperText={errors.accountFiscal || ''}
                    sx={{ ...formStyles }}
                  />
                </Grid2>

                {/* Email */}
                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    id="accountEmail"
                    label={t('admin.accounts.email')}
                    variant={editMode ? 'outlined' : 'standard'}
                    disabled={!editMode}
                    value={values.accountEmail}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!(errors.accountEmail && touched.accountEmail)}
                    helperText={errors.accountEmail || ''}
                    sx={{ ...formStyles }}
                  />
                </Grid2>

                {/* Phone */}
                <Grid2 size={6}>
                  <Box>
                    <PhoneInput
                      containerClass="my-phone-input"
                      country="pt"
                      disabled={!editMode}
                      value={values.accountPhoneNumber}
                      onChange={(phone) =>
                        setFieldValue('accountPhoneNumber', phone.startsWith('+') ? phone : `+${phone}`)
                      }
                      onBlur={() => setFieldValue('accountPhoneNumber', values.accountPhoneNumber, true)}
                      inputStyle={{
                        width: '100%',
                        height: '54px',
                        borderRadius: 8,
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                        border: `1px solid ${
                          errors.accountPhoneNumber && touched.accountPhoneNumber
                            ? theme.palette.error.main
                            : theme.palette.divider
                        }`,
                        paddingLeft: '48px',
                        boxSizing: 'border-box',
                      }}
                      dropdownStyle={{
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                        borderRadius: 8,
                        border: `1px solid ${theme.palette.divider}`,
                        maxHeight: '200px',
                        overflowY: 'auto',
                      }}
                    />
                    {errors.accountPhoneNumber && touched.accountPhoneNumber && (
                      <Typography color="error" variant="caption" sx={{ ml: 1.75, mt: 0.5, display: 'block' }}>
                        {errors.accountPhoneNumber}
                      </Typography>
                    )}
                  </Box>
                </Grid2>

                {/* Address */}
                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    id="accountAddress"
                    label={t('admin.accounts.address')}
                    variant={editMode ? 'outlined' : 'standard'}
                    disabled={!editMode}
                    value={values.accountAddress}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!(errors.accountAddress && touched.accountAddress)}
                    helperText={errors.accountAddress || ''}
                    sx={{ ...formStyles }}
                  />
                </Grid2>

                {/* City */}
                <Grid2 size={4}>
                  <TextField
                    fullWidth
                    id="accountCity"
                    label={t('admin.accounts.city')}
                    variant={editMode ? 'outlined' : 'standard'}
                    disabled={!editMode}
                    value={values.accountCity}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!(errors.accountCity && touched.accountCity)}
                    helperText={errors.accountCity || ''}
                    sx={{ ...formStyles }}
                  />
                </Grid2>

                {/* Zip */}
                <Grid2 size={2}>
                  <TextField
                    fullWidth
                    id="accountZipCode"
                    label={t('admin.accounts.zip-code')}
                    variant={editMode ? 'outlined' : 'standard'}
                    disabled={!editMode}
                    value={values.accountZipCode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!(errors.accountZipCode && touched.accountZipCode)}
                    helperText={errors.accountZipCode || ''}
                    sx={{ ...formStyles }}
                  />
                </Grid2>

                {/* Description */}
                <Grid2 size={12}>
                  <Box>
                    <label style={{ marginBottom: 8, display: 'block' }}>
                      {t('admin.accounts.description')}
                    </label>
                    <RichTextEditor
                      ref={rteRef}
                      extensions={[StarterKit]}
                      content={values.accountDescription}
                      editable={editMode}
                      renderControls={() =>
                        editMode ? (
                          <MenuControlsContainer>
                            <MenuSelectHeading />
                            <MenuDivider />
                            <MenuButtonBold />
                            <MenuButtonItalic />
                          </MenuControlsContainer>
                        ) : null
                      }
                      onUpdate={({ editor }) => setFieldValue('accountDescription', editor.getHTML())}
                    />
                  </Box>
                </Grid2>

                {/* Manager Settings */}
                <Grid2 size={12}>
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, mb: 2 }}>
                      {t('admin.accounts.managers-settings')}
                    </Typography>
                    <Grid2 container spacing={2} alignItems="center">
                      <Grid2 size={{ xs: 12, sm: 4 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              id="allowExternalManagers"
                              checked={values.allowExternalManagers}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              disabled={!editMode || !isAdminUser}
                            />
                          }
                          label={t('admin.accounts.allow-external-managers')}
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 8 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <FormControlLabel
                            control={
                              <Switch
                                id="limitInternalManagers"
                                checked={values.limitInternalManagers}
                                onChange={(e) => {
                                  setFieldValue('limitInternalManagers', e.target.checked);
                                  if (!e.target.checked) setFieldValue('maxInternalManagers', null);
                                }}
                                onBlur={handleBlur}
                                disabled={!editMode || !isAdminUser}
                              />
                            }
                            label={t('admin.accounts.limit-internal-managers')}
                            sx={{ mr: 0, flexShrink: 0 }}
                          />
                          <TextField
                            id="maxInternalManagers"
                            label={t('admin.accounts.max-internal-managers')}
                            type="number"
                            size="small"
                            disabled={!editMode || !values.limitInternalManagers || !isAdminUser}
                            value={values.maxInternalManagers ?? ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '') { setFieldValue('maxInternalManagers', null); return; }
                              setFieldValue('maxInternalManagers', Math.max(0, Number(value)));
                            }}
                            onBlur={handleBlur}
                            error={!!(errors.maxInternalManagers && touched.maxInternalManagers)}
                            helperText={errors.maxInternalManagers || ''}
                            sx={{ width: 275 }}
                          />
                        </Box>
                      </Grid2>
                    </Grid2>
                  </Paper>
                </Grid2>

                {/* Actions */}
                <Grid2 size={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 1 }}>
                  {showSaveActions && (
                    <>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          resetForm();
                          setEditMode(false);
                        }}
                      >
                        {t("admin.accounts.account-cancel")}
                      </Button>

                      <Button type="submit" variant="contained">
                        {t("admin.accounts.account-save")}
                      </Button>
                    </>
                  )}

                  {showEditButton && (
                    <Button
                      variant="contained"
                      onClick={() => setEditMode(true)}
                    >
                      {t("admin.accounts.account-edit")}
                    </Button>
                  )}
                </Grid2>
              </Grid2>
            </Box>
          )}
        </Formik>
      )}
    </Box>
  );
};

export default AccountInformation;